import { useCallback, useEffect } from 'react';
import {
  Outlet,
  useNavigate,
  useMatch,
  useOutletContext,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { PermissionDenied, PermissionEnum, IconLoader } from '@ui/src';
import { useLazyGetEmployeesDictionariesQuery } from '@administration/store/dictionaries/service';
import { useAppSelector } from '@store';
import {
  useLazyGetTransactionsBaseCurrencyQuery,
  useLazyGetTransactionsSettingsQuery,
} from '../../store/dictionaries/services';
import {
  useLazyGetTransactionsEmployeeQuery,
  useGetCurrentEmployeeQuery,
} from './store/services';

import ROUTES from '../../routes';

import cn from './transaction.module.css';

const Transactions = (): React.JSX.Element => {
  const navigate = useNavigate();

  const [getTransactionsBaseCurrency] =
    useLazyGetTransactionsBaseCurrencyQuery();

  const match = useMatch(ROUTES.TRANSACTIONS);
  const [getTransactionsEmployee] = useLazyGetTransactionsEmployeeQuery();
  const [getTransactionsSettings] = useLazyGetTransactionsSettingsQuery();
  const [getEmployeeDictionaries] = useLazyGetEmployeesDictionariesQuery();

  const { sendMessage } = useOutletContext<{
    connectionStatus: string | null;
    sendMessage: SendMessage | null;
  }>();

  const {
    transactionsDictionaries: {
      activePermissions,
      baseCurrency,
      overdraftEnabled,
    },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  useGetCurrentEmployeeQuery();

  const getTransactionsEmployeeAction = useCallback((userId: string) => {
    getTransactionsEmployee(userId);
  }, []);

  useEffect(() => {
    if (
      !users &&
      activePermissions &&
      activePermissions?.includes(PermissionEnum.USERS_LIST)
    ) {
      getEmployeeDictionaries();
    }
  }, [users, activePermissions]);

  useEffect(() => {
    if (!baseCurrency) {
      getTransactionsBaseCurrency();
    }

    if (overdraftEnabled === undefined || overdraftEnabled === null) {
      getTransactionsSettings();
    }
  }, [baseCurrency, overdraftEnabled]);

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.SECTION_TRANSACTIONS)
    ) {
      if (token) {
        const parsed = JSON.parse(token);

        getTransactionsEmployeeAction(parsed.userId);
      }

      if (match) {
        navigate(ROUTES.GENERAL_LEDGER);
      }
    }
  }, [match, activePermissions]);

  useEffect(() => {
    let title = 'Transactions';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content = (
    <div className={cn.transactionLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (activePermissions?.includes(PermissionEnum.SECTION_TRANSACTIONS)) {
      content = <Outlet context={{ sendMessage }} />;
    } else {
      content = <PermissionDenied />;
    }
  }

  return content;
};

const TransactionsWrapper = () => <Transactions />;

export default TransactionsWrapper;
